import { InstallmentOption, useCalculateInstallmentOptions } from "../../../../../helpers/calculationUtils";
import { useToastContext } from "../../../../../../../common/components/ui/toast/toast.context";
import ButtonWithLoader from "../../../../../../../common/components/ui/loading/ButtonLoading";
import SlideToggle from "../../../../../../../common/components/ui/toggleSwitch/SlideToggle";
import { IRenter } from "../../../../../../../types/renter.type";
import { FormikProvider, getIn, useFormik, useFormikContext } from "formik";
import DynamicFields from "./field/DynamicFields";
import { useEffect, useMemo, useState } from "react";
import * as Yup from 'yup';
import RegisterPhotos from "./field/RegisterPhotos";
import { CancellationDetails } from "./field/CancellationDetails";
import AditionalValuesCancelPlan from "./field/AdditionalValuesCancelPlan";
import PaymentDetails from "./field/PaymentDetails";
import { updateVehicleVisualRecords } from "../../../../../../../common/services/vehicles.api";
import { cancelSubscription } from "../../../../../../../common/services/renters.api";
import { BILLING_TYPE } from "@/types/payment.type";

const validationSchema = Yup.object().shape({
  reason: Yup.string().required('Motivo é obrigatório'),
  additionalComments: Yup.string().required('Comentarios adicionais é obrigatório'),
  paymentMethod: Yup.string().when('valueTotalBoolean', {
    is: true,
    then: (schema) => schema.required('Método de pagamento é obrigatório'),
  }),
  paymentDescription: Yup.string().when('valueTotalBoolean', {
    is: true,
    then: (schema) => schema.required('Descrição do pagamento é obrigatória'),
  }),
  billingPeriod: Yup.string().when('paymentMethod', {
    is: (value) => value === 'credit' || value === 'boleto',
    then: (schema) => schema.required('Período de cobrança é obrigatório'),
  }),
  installmentOption: Yup.string().when('paymentMethod', {
    is: (value) => value === 'credit' || value === 'boleto',
    then: (schema) => schema.required('Período de cobrança é obrigatório'),
  }),
  subscriptionInitialDate: Yup.date().when('paymentMethod', {
    is: (value) => value === 'credit' || value === 'boleto',
    then: (schema) => schema.required('Data de cobrança é obrigatória'),
  }),
})

type Props = {
  data: IRenter
  vehicleId: string
  daysRemaining: number
  repossessionDate: Date
}

export default function CancelPlanModal(props: Props) {
  const [isChecked, setIsChecked] = useState(false);
  const { notifySuccess, notifyError } = useToastContext()
  const renter = props.data;
  const vehicleId = props.vehicleId;
  const firstInstallmentDate = renter.activationData?.plan?.subscriptionInitialDate;
  const caucao = renter.activationData.plan?.deposit;
  const formikContext = useFormikContext()
  const registers = getIn(formikContext.values, 'registers')
  const nameRenter = getIn(formikContext.values, 'renterName')
  const returnReason = getIn(formikContext.values, 'returnReason')
  const licensePlate = getIn(formikContext.values, 'licensePlate')

  const onAddRegister = () => {
    formikContext.setFieldValue('registers', [...registers, { type: '', content: '', verified: false }])
  }

  const valueWeeks = renter.activationData.plan?.value || 0;
  const dailyValue = parseFloat(renter?.activationData?.plan?.price?.replace('R$', '').replace(',', '.') || "0");

  const paymentOptions = [
    { value: "-", label: "-" },
    { value: BILLING_TYPE.PIX, label: "PIX( Para Pagamento na hora)" },
    { value: BILLING_TYPE.CREDIT_CARD, label: "CRÉDITO (Conferir no Asaas)" },
    { value: BILLING_TYPE.BOLETO, label: "BOLETO" },
  ];

  const daysRemaining = props.daysRemaining
  const finePlanValue = ((dailyValue * daysRemaining) * 0.10).toFixed(2);

  const formik = useFormik({

    initialValues: {
      fields: [], // Inicializa com um campo vazio
      reason: "",
      caucao: caucao,
      sinister: 0,
      dailyValue: dailyValue,
      valueWeeks: valueWeeks,
      billingPeriod: "",
      paymentMethod: "",
      installmentOption: "",
      paymentOptions: paymentOptions,
      isCheckedCommandBlock: false,
      isPostWeekChecked: false,
      additionalComments: "",
      paymentDescription: "",
      subscriptionInitialDate: firstInstallmentDate,
      valueTotalBoolean: false,
      finalFineValue: 0, // Campo para receber o valor total calculado
      amountValue: 0,
      isCheckedRetraction: false,
      // Outras inicializações...
    },
    validationSchema: validationSchema,
    onSubmit: async (data) => {

      const receivedData = {
        renterId: renter._id,
        fineDetran: data.fields,  // Garanta que isso é um array de objetos com 'value' e 'description'
        additionalComments: data.additionalComments,
        paymentDescription: data.paymentDescription,
        valueBlockCommand: data.isCheckedCommandBlock,
        vehicleId: vehicleId,
        customerId: renter.externalReferences.paymentCustomerId,
        paymentMethod: data.paymentMethod,
        installmentOptions: data.installmentOption,
        isCheckedRetraction: data.isCheckedRetraction,
        sinister: data.sinister,
        billingPeriod: data.billingPeriod,
        subscriptionInitialDate: data.subscriptionInitialDate,
        caucao: caucao?.toString(),
        finalFineValue: data.finalFineValue,
        dailyValue: dailyValue,
        finePlanValue: parseFloat(finePlanValue),
        reason: data.reason,
        amoutnValue: data.amountValue,
        isPostWeekChecked: data.isPostWeekChecked,
        valueBilling: Math.max(0, data.finalFineValue - (caucao || 0)),
      };

      const result = await cancelSubscription(receivedData)
      if (!result.ok) {
        // Verifica se `result.messages` é um array e percorre cada mensagem
        if (Array.isArray(result.message)) {
          result.message.forEach((message: string) => {
            notifyError(message); // Exibe cada mensagem de erro no toast
          });
        } else {
          // Se `messages` não for um array, exibe a mensagem normalmente
          notifyError(result.message as string);
        }
      } else {
        notifySuccess("Contrato cancelado com sucesso"); // Exibe mensagem de sucesso
      }

      if (result.ok) {
        const savePhotoDelivery = {
          renterId: renter._id,
          vehicleId: vehicleId,
          photos: registers,
          nameRenter: nameRenter,
          licensePlate: licensePlate,
          returnReason: returnReason,
        }
        const resultPhoto = await updateVehicleVisualRecords(savePhotoDelivery)
        if (!resultPhoto.ok) {
          notifyError(resultPhoto.message as string)
          return
        }
        notifySuccess("Fotos salvas com sucesso");
      }
    }
  })

  // Callback para receber o total calculado do componente de valores adicionais
  const handleTotalChange = ({ initalValue, amountValue, isCheckedRetraction, isCheckedCommandBlock, valuesSinister, isPostWeekChecked }:
    {
      initalValue: number;
      amountValue: number;
      isCheckedRetraction: boolean;
      isCheckedCommandBlock: boolean;
      valuesSinister: number;
      isPostWeekChecked: boolean;

    }) => {
    formik.setFieldValue('finalFineValue', initalValue);
    formik.setFieldValue('amountValue', amountValue);
    formik.setFieldValue('isCheckedRetraction', isCheckedRetraction);
    formik.setFieldValue('isCheckedCommandBlock', isCheckedCommandBlock);
    formik.setFieldValue('sinister', valuesSinister);
    formik.setFieldValue('isPostWeekChecked', isPostWeekChecked);
    // Se necessário, você pode atualizar outro campo com o amountValue
    // formik.setFieldValue('amountValue', amountValue);
  };

  const valueTotal = formik.values.finalFineValue;
  useEffect(() => {
    formik.setFieldValue('valueTotalBoolean', valueTotal > caucao ? true : false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueTotal]);

  const calculateInstallmentOptions = useCalculateInstallmentOptions();
  const installmentOptions: InstallmentOption[] = useMemo(() => {
    return calculateInstallmentOptions((valueTotal - caucao), 24);
  }, [valueTotal, caucao, calculateInstallmentOptions]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>

        <section>
          <CancellationDetails
            finePlanValue={finePlanValue}
            repossessionDate={props.repossessionDate}
            cancellationReasonOptions={[
              { value: "", label: "-" },
              { value: "Inadimplencia", label: "Inadimplência" },
              { value: "quebraContrato", label: "Quebra de Contrato" },
              { value: "recolhimento", label: "Mal uso" },
            ]}
          />
        </section>
        <section>
          <AditionalValuesCancelPlan
            onTotalChange={handleTotalChange}
            valueWeeks={valueWeeks}
            dailyFine={dailyValue}
            finePlanValue={finePlanValue}
          />

        </section>

        <div className="pb-3 mt-2" style={{ display: 'flex', alignItems: 'center' }}>
          <label className="font-semibold text-[14px]"> O cliente tem multas de transito? </label>
          <SlideToggle
            name="isChecked"
            className="ml-2"
            onToggle={(checked) => {
              setIsChecked(checked);
              formik.setFieldValue("isChecked", checked);
            }}
          />          <span style={{ marginLeft: '10px' }}>{isChecked ? "Sim" : "Não"}</span>
        </div>

        <div>
          {isChecked && <DynamicFields />}
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="additionalComments" className="block text-sm font-medium text-gray-600">
            Descreva o motivo do cancelamento:
          </label>
          <textarea
            id="additionalComments"
            name="additionalComments"
            value={formik.values.additionalComments}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            style={{ resize: "none", height: "90px" }}
          />

          {!formik.touched.additionalComments && formik.errors.additionalComments && (
            <div className="text-redPrimary text-sm">{formik.errors.additionalComments}</div>
          )}
        </div>
        <section>
          <PaymentDetails
            caucao={caucao}
            valueTotal={formik.values.finalFineValue}
            paymentOptions={paymentOptions}
            installmentOptions={installmentOptions}
            amountValue={formik.values.amountValue}
            modal={"CancelPlanModal"}
          />

        </section>
      </FormikProvider>
      <RegisterPhotos
        title={"Inspeção de Veículo"}  // Usar um título padrão caso não seja fornecido
        items={registers}
        vehicleId={vehicleId}  // Garantindo que o ID do veículo esteja sempre disponível
        renterId={renter._id!}
        nameRenter={nameRenter}
        onAddItem={onAddRegister}  // Método para adicionar um registro
        updateStatus={undefined}  // Se você tiver uma função para atualizar o status, passe aqui
        titleTextArea={'Descreva as avarias da moto:'}
      />
      <div className="flex justify-end mt-4">
        <ButtonWithLoader
          type="button" isLoading={formik.isSubmitting} disabled={!formik.isValid} onClick={formik.handleSubmit}>
          Finalizar Plano
        </ButtonWithLoader>
      </div>
    </form >
  )
}